(ns ui.view.transaction-process.modals.attestation.model
  "Pure state helpers for the attestation modal. The form draft lives under the
  TP state-path at `[:ui :modal-form]`. `pending-files` holds raw JS File
  objects awaiting upload after the entity is persisted."
  (:require
   [clojure.set :as set]
   [frontend.utils :as fu]))

(defn init-create-state
  "The draft keys its entity values by the real `:attestation/*` attributes (not
  parallel short keys), so the form reads directly as an entity — no translation
  layer for the mandatoriness/gate helpers or the save payload. The remaining
  plain keys are transient modal meta (no entity-attr collision). `slot-kw` is the
  TP named ref this attestation links to (sent as `:slot-attr` on create)."
  [{:keys [slot-kw attestation-type-label prefill]}]
  (merge
   {:edit-mode?              false
    :slot-kw                 slot-kw
    :attestation-type-label  attestation-type-label
    :conditions              []
    :pending-files           []
    :draft?                  true
    :submitting?             false
    :error                   nil
    :attestation/outcome     nil
    :attestation/date        nil
    :attestation/comment     ""}
   ;; Template-driven create defaults (resolved `:slot/prefill`, e.g.
   ;; `:attestation/date` -> today). Keyed by real entity attrs so they land
   ;; straight on the draft.
   prefill))

(defn init-edit-state
  "Pure: caller resolves the attestation entity and its condition entities from
  session and passes them in (`conditions` already shaped as edit rows). `slot-kw`
  is supplied by the caller; the slot identity comes from the layout/panel that
  opened the modal."
  [{:keys [attestation conditions slot-kw attestation-type-label]}]
  {:edit-mode?             true
   :entity-id              (:db/id attestation)
   :original               attestation
   :slot-kw                slot-kw
   :attestation-type-label attestation-type-label
   :conditions             (vec conditions)
   :pending-files          []
   :draft?                 (boolean (:attestation/draft? attestation))
   :submitting?            false
   :error                  nil
   :attestation/outcome    (:attestation/outcome attestation)
   :attestation/date       (:attestation/date attestation)
   :attestation/comment    (or (:attestation/comment attestation) "")})

(defn condition->edit-row
  "Shape a resolved attestation-condition entity into a form edit row."
  [c]
  {:db/id       (:db/id c)
   :title       (:attestation-condition/title c)
   :description (:attestation-condition/description c)
   :status      (:attestation-condition/status c)})

(defn ->create-payload
  "Build the {:attestation-id :entities} payload for create. Conditions with a
  blank title are dropped. The slot the attestation links to is sent as
  `:slot-attr` (the TP named ref) by the create event."
  [{:keys [conditions draft?]
    :attestation/keys [outcome date comment]}]
  (let [attestation-id (fu/create-temp-id "temp-attestation-")
        attestation    (cond-> {:db/id                        attestation-id
                                :entity/type                  :entity.type/attestation
                                :attestation/outcome          outcome
                                :attestation/draft?           (boolean draft?)}
                         date          (assoc :attestation/date date)
                         (seq comment) (assoc :attestation/comment comment))
        condition-entries (->> conditions
                               (filter (fn [c] (seq (:title c))))
                               (mapv (fn [c]
                                       (let [cid (fu/create-temp-id "temp-condition-")]
                                         [cid (cond-> {:db/id                             cid
                                                       :entity/type                       :entity.type/attestation-condition
                                                       :attestation-condition/title       (:title c)
                                                       :attestation-condition/description (:description c)}
                                                (:status c) (assoc :attestation-condition/status (:status c)))]))))
        condition-ids (mapv first condition-entries)
        attestation   (cond-> attestation
                        (seq condition-ids) (assoc :attestation/conditions condition-ids))]
    {:attestation-id attestation-id
     :entities       (into {attestation-id attestation} condition-entries)}))

(defn condition-row->create-data
  [row]
  (cond-> {:entity/type                       :entity.type/attestation-condition
           :attestation-condition/title       (:title row)
           :attestation-condition/description (:description row)}
    (:status row) (assoc :attestation-condition/status (:status row))))

(defn condition-row->update-data
  [{:keys [title description status]}]
  {:attestation-condition/title       title
   :attestation-condition/description description
   :attestation-condition/status      status})

(defn- condition-ref-id
  [ref]
  (or (:db/id ref) ref))

(defn condition-diff
  "Compare edit form rows to the original attestation refs. Blank-title rows are
  not kept. Existing blank rows are treated as removed; new blank rows are
  ignored."
  [{:keys [original conditions]}]
  (let [original-ids (->> (:attestation/conditions original)
                          (map condition-ref-id)
                          (filter int?)
                          (set))
        active-rows  (->> conditions
                          (filter (fn [row] (seq (:title row)))))
        existing     (->> active-rows
                          (filter (fn [row] (int? (:db/id row)))))
        existing-ids (->> existing (map :db/id) (set))
        new-rows     (->> active-rows
                          (remove (fn [row] (int? (:db/id row)))))
        removed-ids  (set/difference original-ids existing-ids)]
    {:kept-condition-ids (vec (map :db/id existing))
     :removed-ids        (vec removed-ids)
     :condition-updates  (mapv (fn [row]
                                 {:db/id (:db/id row)
                                  :data  (condition-row->update-data row)})
                               existing)
     :condition-creates  (mapv (fn [row]
                                 (let [condition-id (fu/create-temp-id "temp-condition-")]
                                   {:temp-id condition-id
                                    :entity  (condition-row->create-data row)}))
                               new-rows)}))

(defn- original-comment
  [original]
  (or (:attestation/comment original) ""))

(defn ->update-attrs
  [{:keys [draft? original] :attestation/keys [outcome date comment]}]
  (cond-> {:attestation/outcome outcome
           :attestation/draft?  (boolean draft?)}
    (not= (:attestation/date original) date)
    (assoc :attestation/date date)

    (not= (original-comment original) comment)
    (assoc :attestation/comment (not-empty comment))))
